import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function pad(n) {
  return String(n).padStart(2, '0');
}

function isoDeCelda(anio, mesIndex, dia) {
  return `${anio}-${pad(mesIndex + 1)}-${pad(dia)}`;
}

export default function Vacaciones() {
  const hoy = new Date();
  const [mesVista, setMesVista] = useState(hoy.getMonth());
  const [anioVista, setAnioVista] = useState(hoy.getFullYear());
  const [dias, setDias] = useState([]); // ['YYYY-MM-DD', ...] del año en vista
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(null); // fecha en curso de guardado
  const [msg, setMsg] = useState(null);

  const fetchDias = useCallback(async (anio) => {
    setLoading(true);
    try {
      const res = await api.get(`/vacaciones/mias?anio=${anio}`);
      setDias(res.data.dias || []);
    } catch {
      setMsg({ tipo: 'err', texto: 'No se pudieron cargar tus vacaciones' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDias(anioVista);
  }, [anioVista, fetchDias]);

  const diasSet = useMemo(() => new Set(dias), [dias]);

  const cambiarMes = (delta) => {
    let m = mesVista + delta;
    let a = anioVista;
    if (m < 0) { m = 11; a -= 1; }
    if (m > 11) { m = 0; a += 1; }
    setMesVista(m);
    if (a !== anioVista) setAnioVista(a);
  };

  const toggleDia = async (fecha) => {
    if (guardando) return;
    setGuardando(fecha);
    setMsg(null);
    const marcado = diasSet.has(fecha);
    try {
      if (marcado) {
        await api.delete(`/vacaciones/mias/${fecha}`);
        setDias((prev) => prev.filter((f) => f !== fecha));
      } else {
        await api.post('/vacaciones/mias', { fecha });
        setDias((prev) => [...prev, fecha].sort());
      }
    } catch (err) {
      setMsg({ tipo: 'err', texto: err.response?.data?.error || 'No se pudo actualizar el día' });
    } finally {
      setGuardando(null);
    }
  };

  // Construye la cuadrícula del mes en vista (semanas de lunes a domingo)
  const celdas = useMemo(() => {
    const primerDia = new Date(anioVista, mesVista, 1);
    const diasEnMes = new Date(anioVista, mesVista + 1, 0).getDate();
    const offset = (primerDia.getDay() + 6) % 7; // lunes = 0

    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= diasEnMes; d++) arr.push(d);
    return arr;
  }, [mesVista, anioVista]);

  const diasDelMesMarcados = dias.filter((f) => f.startsWith(`${anioVista}-${pad(mesVista + 1)}`)).length;
  const hoyIso = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-${pad(hoy.getDate())}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-1">🏖️ Mis Vacaciones</h3>
        <p className="text-xs text-gray-500">
          Toca un día para marcarlo o desmarcarlo como vacaciones. Se reflejará en los informes de jornada.
        </p>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium
          ${msg.tipo === 'ok' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.texto}
        </div>
      )}

      <div className="card">
        {/* Navegación mes/año */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => cambiarMes(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            ‹
          </button>
          <div className="font-semibold text-gray-800 capitalize">
            {MESES[mesVista]} {anioVista}
          </div>
          <button
            onClick={() => cambiarMes(1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            ›
          </button>
        </div>

        {/* Días de la semana */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
          ))}
        </div>

        {/* Cuadrícula */}
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Cargando...</div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {celdas.map((dia, i) => {
              if (dia === null) return <div key={`b${i}`} />;
              const fecha = isoDeCelda(anioVista, mesVista, dia);
              const marcado = diasSet.has(fecha);
              const esHoy = fecha === hoyIso;
              const enCurso = guardando === fecha;

              return (
                <button
                  key={fecha}
                  onClick={() => toggleDia(fecha)}
                  disabled={!!guardando}
                  className={`aspect-square rounded-lg text-sm font-medium flex items-center justify-center transition-colors relative
                    disabled:cursor-wait
                    ${marcado
                      ? 'bg-amber-400 text-amber-900 hover:bg-amber-500'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}
                    ${esHoy ? 'ring-2 ring-mgh-blue-900 ring-offset-1' : ''}`}
                >
                  {enCurso ? (
                    <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : dia}
                </button>
              );
            })}
          </div>
        )}

        {/* Leyenda */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-amber-400 inline-block" />
            De vacaciones
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-gray-50 border border-gray-200 inline-block" />
            Día normal
          </div>
        </div>
      </div>

      <div className="card text-center">
        <div className="text-2xl font-black text-mgh-blue-900">{diasDelMesMarcados}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {diasDelMesMarcados === 1 ? 'día de vacaciones en' : 'días de vacaciones en'} {MESES[mesVista].toLowerCase()}
        </div>
      </div>
    </div>
  );
}
